import { FC, SVGProps } from 'react';

type IconProps = {
	size?: number;
	sw?: number;
	color?: string;
	title?: string;
} & Omit<SVGProps<SVGSVGElement>, 'width' | 'height'>;

const base = ({
	size = 16,
	sw = 1.6,
	color = 'currentColor',
	...rest
}: IconProps): SVGProps<SVGSVGElement> & {
	width: number;
	height: number;
} => ({
	width: size,
	height: size,
	viewBox: '0 0 24 24',
	fill: 'none',
	stroke: color,
	strokeWidth: sw,
	strokeLinecap: 'round',
	strokeLinejoin: 'round',
	...rest,
});

export const IconPhone: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z' />
	</svg>
);

export const IconPhoneOff: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='m21 15.46-5.27-.61-2.52 2.52' />
		<path d='M5 12.55a11 11 0 0 1 3.23-2.1' />
		<path d='M15.09 8.91A11 11 0 0 1 19 12.55' />
		<path d='M3 3l18 18' />
		<path d='M15.69 20.69 12 17l-3.58 1a15.78 15.78 0 0 1-3.55-5.5' />
	</svg>
);

export const IconMic: FC<IconProps> = props => (
	<svg {...base(props)}>
		<rect x='9' y='2' width='6' height='12' rx='3' />
		<path d='M5 11a7 7 0 0 0 14 0' />
		<path d='M12 18v4' />
	</svg>
);

export const IconMicOff: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M3 3l18 18' />
		<path d='M9 9v4a3 3 0 0 0 5.12 2.12' />
		<path d='M15 9.34V5a3 3 0 0 0-5.68-1.33' />
		<path d='M17 16.95A7 7 0 0 1 5 12v-1' />
		<path d='M19 11a7 7 0 0 1-.11 1.23' />
		<path d='M12 18v4' />
	</svg>
);

export const IconSend: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M22 2 11 13' />
		<path d='M22 2l-7 20-4-9-9-4 20-7z' />
	</svg>
);

export const IconPin: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M12 17v5' />
		<path d='M9 10.76V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v5.76a2 2 0 0 0 1.11 1.79l1.78.9A2 2 0 0 1 19 14.24V16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-1.76a2 2 0 0 1 1.11-1.79l1.78-.9A2 2 0 0 0 9 10.76z' />
	</svg>
);

export const IconPinFill: FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
	<svg
		width={size}
		height={size}
		viewBox='0 0 24 24'
		fill={color}
		stroke={color}
		strokeWidth='1.6'
		strokeLinejoin='round'
	>
		<path d='M9 10.76V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v5.76a2 2 0 0 0 1.11 1.79l1.78.9A2 2 0 0 1 19 14.24V16a1 1 0 0 1-1 1h-5v5l-1 1-1-1v-5H6a1 1 0 0 1-1-1v-1.76a2 2 0 0 1 1.11-1.79l1.78-.9A2 2 0 0 0 9 10.76z' />
	</svg>
);

export const IconX: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M18 6 6 18' />
		<path d='m6 6 12 12' />
	</svg>
);

export const IconPlus: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M12 5v14' />
		<path d='M5 12h14' />
	</svg>
);

export const IconSun: FC<IconProps> = props => (
	<svg {...base(props)}>
		<circle cx='12' cy='12' r='4' />
		<path d='M12 2v2' />
		<path d='M12 20v2' />
		<path d='m4.93 4.93 1.41 1.41' />
		<path d='m17.66 17.66 1.41 1.41' />
		<path d='M2 12h2' />
		<path d='M20 12h2' />
		<path d='m6.34 17.66-1.41 1.41' />
		<path d='m19.07 4.93-1.41 1.41' />
	</svg>
);

export const IconMoon: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z' />
	</svg>
);

export const IconSettings: FC<IconProps> = props => (
	<svg {...base(props)}>
		<circle cx='12' cy='12' r='3' />
		<path d='M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z' />
	</svg>
);

export const IconMapPin: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M20 10c0 7-8 13-8 13s-8-6-8-13a8 8 0 0 1 16 0z' />
		<circle cx='12' cy='10' r='3' />
	</svg>
);

export const IconGlobe: FC<IconProps> = props => (
	<svg {...base(props)}>
		<circle cx='12' cy='12' r='10' />
		<path d='M2 12h20' />
		<path d='M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z' />
	</svg>
);

export const IconClock: FC<IconProps> = props => (
	<svg {...base(props)}>
		<circle cx='12' cy='12' r='10' />
		<path d='M12 6v6l4 2' />
	</svg>
);

export const IconMail: FC<IconProps> = props => (
	<svg {...base(props)}>
		<rect x='2' y='4' width='20' height='16' rx='2' />
		<path d='m22 7-10 6L2 7' />
	</svg>
);

export const IconChevronDown: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='m6 9 6 6 6-6' />
	</svg>
);

export const IconChevronRight: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='m9 6 6 6-6 6' />
	</svg>
);

export const IconMore: FC<IconProps> = props => (
	<svg {...base(props)}>
		<circle cx='12' cy='12' r='1' />
		<circle cx='19' cy='12' r='1' />
		<circle cx='5' cy='12' r='1' />
	</svg>
);

export const IconEdit: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M12 20h9' />
		<path d='M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z' />
	</svg>
);

export const IconTrash: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M3 6h18' />
		<path d='M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' />
		<path d='M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6' />
	</svg>
);

export const IconSparkles: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M12 3v4' />
		<path d='M12 17v4' />
		<path d='M3 12h4' />
		<path d='M17 12h4' />
		<path d='m5.64 5.64 2.83 2.83' />
		<path d='m15.54 15.54 2.83 2.83' />
		<path d='m5.64 18.36 2.83-2.83' />
		<path d='m15.54 8.46 2.83-2.83' />
	</svg>
);

export const IconCheck: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M20 6 9 17l-5-5' />
	</svg>
);

export const IconZap: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M13 2 3 14h9l-1 8 10-12h-9l1-8z' />
	</svg>
);

export const IconPaperclip: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48' />
	</svg>
);

export const IconBook: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M4 19.5A2.5 2.5 0 0 1 6.5 17H20' />
		<path d='M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z' />
	</svg>
);

export const IconUser: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2' />
		<circle cx='12' cy='7' r='4' />
	</svg>
);

export const IconCalendarNote: FC<IconProps> = props => (
	<svg {...base(props)}>
		<rect x='3' y='4' width='18' height='18' rx='2' />
		<path d='M16 2v4' />
		<path d='M8 2v4' />
		<path d='M3 10h18' />
		<path d='M8 14h6' />
		<path d='M8 18h4' />
	</svg>
);

export const IconLinkedIn: FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
	<svg width={size} height={size} viewBox='0 0 24 24' fill={color} stroke='none'>
		<path d='M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' />
	</svg>
);

export const IconTwitter: FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
	<svg width={size} height={size} viewBox='0 0 24 24' fill={color} stroke='none'>
		<path d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' />
	</svg>
);

// HubSpot mark. Filled (not stroked) so it reads correctly at icon sizes.
// Default color matches HubSpot brand orange.
export const IconHubSpot: FC<IconProps> = ({ size = 16, color = '#FF7A59' }) => (
	<svg width={size} height={size} viewBox='0 0 640 640' fill={color} stroke='none'>
		<path d='M331.8 275.6C306.7 299.3 291 332.9 291 370.2C291 399.5 300.7 426.5 317 448.2L267.5 498C263.1 496.4 258.4 495.5 253.5 495.5C242.7 495.5 232.6 499.7 225 507.3C217.4 514.9 213.2 525.1 213.2 535.9C213.2 546.7 217.4 556.8 225 564.4C232.6 572 242.8 576 253.5 576C264.3 576 274.4 572.1 282.1 564.4C289.7 556.8 293.9 546.6 293.9 535.9C293.9 531.7 293.3 527.7 292 523.8L342 473.6C364 490.5 391.4 500.5 421.3 500.5C493.2 500.5 551.3 442.2 551.3 370.3C551.3 305.1 503.6 251.1 441.1 241.6L441.1 180C458.6 172.6 469.3 156.2 469.3 137.1C469.3 111 448.4 89.2 422.3 89.2C396.2 89.2 375.6 111 375.6 137.1C375.6 156.2 386.3 172.6 403.8 180L403.8 241.2C388.6 243.3 374.2 247.9 361.1 254.8C333.5 233.9 243.6 169.1 192.2 130C193.4 125.6 194.2 121 194.2 116.2C194.2 87.4 170.7 64 141.8 64C113 64 89.6 87.4 89.6 116.2C89.6 145.1 113 168.5 141.8 168.5C151.6 168.5 160.7 165.6 168.6 160.9L331.8 275.6zM421.3 301.2C459.4 301.2 490.3 332.1 490.3 370.2C490.3 408.3 459.4 439.2 421.3 439.2C383.2 439.2 352.3 408.3 352.3 370.2C352.3 332.1 383.2 301.2 421.3 301.2z' />
	</svg>
);

export const IconGoogle: FC<IconProps> = ({ size = 16 }) => (
	<svg width={size} height={size} viewBox='0 0 48 48' fill='none' stroke='none'>
		<path
			fill='#EA4335'
			d='M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z'
		/>
		<path
			fill='#4285F4'
			d='M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z'
		/>
		<path
			fill='#FBBC05'
			d='M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z'
		/>
		<path
			fill='#34A853'
			d='M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z'
		/>
	</svg>
);

export const IconSearch: FC<IconProps> = props => (
	<svg {...base(props)}>
		<circle cx='11' cy='11' r='7' />
		<path d='m21 21-4.3-4.3' />
	</svg>
);

export const IconCopy: FC<IconProps> = props => (
	<svg {...base(props)}>
		<rect x='9' y='9' width='13' height='13' rx='2' />
		<path d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1' />
	</svg>
);

export const IconMessageSquare: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
	</svg>
);

export const IconFileText: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
		<path d='M14 2v6h6' />
		<path d='M9 13h6' />
		<path d='M9 17h6' />
	</svg>
);

// 3x3 grid — used for the in-call DTMF dialpad toggle.
export const IconGrid: FC<IconProps> = props => (
	<svg {...base(props)}>
		<rect x='3' y='3' width='7' height='7' />
		<rect x='14' y='3' width='7' height='7' />
		<rect x='3' y='14' width='7' height='7' />
		<rect x='14' y='14' width='7' height='7' />
	</svg>
);

// Trophy — leaderboard nav link.
export const IconTrophy: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M6 9H4.5a2.5 2.5 0 0 1 0-5H6' />
		<path d='M18 9h1.5a2.5 2.5 0 0 0 0-5H18' />
		<path d='M4 22h16' />
		<path d='M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22' />
		<path d='M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22' />
		<path d='M18 2H6v7a6 6 0 0 0 12 0V2Z' />
	</svg>
);

// Bell — follow-ups nav link (scheduled reminders).
export const IconBell: FC<IconProps> = props => (
	<svg {...base(props)}>
		<path d='M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9' />
		<path d='M10.3 21a1.94 1.94 0 0 0 3.4 0' />
	</svg>
);
